import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useEntitlementsLoading,
  useHasModule,
  type AddonModule,
} from '@/shared/hooks/use-entitlements';

interface RequireEntitlementProps {
  module: AddonModule;
  children: ReactNode;
}

/**
 * Route guard for paid add-on modules (admin-controlled entitlements). Redirects
 * to the dashboard with a toast when the tenant's plan does not include the
 * module — mirrors the backend `requireEntitlement` 402 gate.
 */
export function RequireEntitlement({
  module,
  children,
}: RequireEntitlementProps): JSX.Element | null {
  const hasModule = useHasModule(module);
  const isLoading = useEntitlementsLoading();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoading && !hasModule) {
      toast.warning(t('common.featureNotInPlan'));
      void navigate('/', { replace: true });
    }
  }, [isLoading, hasModule, navigate, t]);

  if (isLoading) return null;
  if (!hasModule) return null;
  return <>{children}</>;
}
